import { useEffect } from 'react'
import { ApolloClient } from '@apollo/client'
import { setCookie } from 'cookies-next'
import { Maybe } from 'graphql/jsutils/Maybe'
import { ParsedUrlQuery } from 'querystring'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { useLegacyEffect } from '@/molecules/utils'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getContentfulClient } from '@/services/ApolloClient'
import { getPageByPath } from '@/services/CmsService/Pages'
import { ValidSale } from '@/services/ContentfulSaleService'
import { getGuildPromotion, Promotion } from '@/services/GuildPromotionService'
import {
  getGuildPricingTicketsOption,
  getMarketingOfferPageBySlug,
  GuildEarlyAccessSectionModel,
  GuildMarketingOfferPageData,
  GuildPricingTicketsOption,
} from '@/services/GuildSignupPageService'
import {
  GuildProjectSlugs,
  GuildSignupEmphasis,
  GuildSignupLayout,
} from '@/services/GuildSignupPageService/GuildSignupPageTypes'
import { isGuildSignupSimpleLayout } from '@/services/GuildSignupPageService/validations'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { fetchPageDataContext, PageDataContext, PageModel } from '@/services/RenderService'
import { GuildSale, transformContentfulSaleToValidatedSale } from '@/services/SaleService'
import { GuildSignupExplainerLayout, GuildTestimonialSection } from '@/types/codegen-contentful'
import { useSafeTrack } from '@/utils/analytics'
import { useStickyAttribution } from '@/utils/analytics/StickyAttribution'
import { projectSlugsAttribution } from '@/utils/analytics/attribution'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { omitUndefineds } from '@/utils/object'
import { writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildStatus } from '@/utils/users/usersServer'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'
import { MarketingWinbackView } from '@/views/MarketingWinbackView'

interface GuildMarketingOfferProps {
  emphasis: GuildSignupEmphasis
  guildMemberCta: string
  guildMemberHref: string
  guildExplainerLayout?: GuildSignupExplainerLayout | null
  layout: GuildSignupLayout
  locale: string
  projectSlugs?: GuildProjectSlugs
  promotion: Promotion | null
  sale: GuildSale | null
  slug: string
  guildTestimonialSection?: GuildTestimonialSection
  guildEarlyAccessSection?: GuildEarlyAccessSectionModel
  hasUser: boolean
  logo?: {
    src: string
    alt: string
    className?: string
  }
  experiments?: ServerExperiments
  region: string | null
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
  isDarkMode?: boolean
  guildPricingTicketsOption?: Maybe<GuildPricingTicketsOption>
}

export const GuildMarketingOfferPage: NextPageWithLayout<GuildMarketingOfferProps> = ({
  emphasis,
  guildMemberCta,
  guildMemberHref,
  guildExplainerLayout,
  layout,
  locale,
  projectSlugs,
  promotion,
  sale,
  slug,
  guildTestimonialSection,
  guildEarlyAccessSection,
  logo,
  experiments,
  region,
  page,
  pageDataContext,
  guildPricingTicketsOption,
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()

  useLegacyEffect(() => {
    track('Guild Product Page Viewed', { pageContext: emphasis, slug })
    setCookie('guildFocus', slug, { maxAge: Durations.ONE_YEAR_IN_SECONDS })
  }, [track, slug, emphasis])

  useEffect(() => {
    writeToSessionStorage('pageSlug', slug)
    writeToSessionStorage('guildCheckoutOrigin', `/m/offer/${slug}`)
    projectSlugsAttribution(projectSlugs)
  }, [slug, projectSlugs])

  useStickyAttribution('guild-attribution')

  return (
    <div className={isGuildSignupSimpleLayout(layout) ? 'bg-white' : 'bg-core-gray-950'}>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1756153598/angel-studios/seo/angel-opengraph-5',
          cloudinaryTransformation: 'b_rgb:000000',
          description: t(
            'angelStudiosGuildJoinInfoMetaDescriptionV3',
            'Join the Angel Guild to vote on upcoming shows, get free tickets to theatrical releases, early access, and merchandise discounts.',
          ),
          locale,
          path: `${paths.marketing.offer}/${slug}`,
          title: t('angelGuildEarlyAccessMetaTitleV2', 'Angel Guild Membership | Angel'),
        })}
        noindex
        nofollow
      />
      <GuildSignupContextProvider
        guildTestimonialSection={guildTestimonialSection}
        guildEarlyAccessSection={guildEarlyAccessSection}
        emphasis={emphasis}
        guildExplainerLayout={guildExplainerLayout}
        guildMemberCta={guildMemberCta}
        guildMemberHref={guildMemberHref}
        layout={layout}
        projectSlugs={projectSlugs}
        promotion={promotion}
        sale={sale}
        slug={slug}
        logo={logo}
        experiments={experiments}
        region={region}
        guildPricingTicketsOption={guildPricingTicketsOption}
      >
        <MarketingWinbackView page={page} pageDataContext={pageDataContext} />
        <MarketingVideoPopup ctaHref={'#plan-selection'} ctaText={t('joinTheGuild', 'Join the Guild')} />
      </GuildSignupContextProvider>
    </div>
  )
}

function shouldRedirectToDashboard(guildStatus: GuildStatus | null, emphasis: string) {
  if (!guildStatus) return false
  return (
    guildStatus &&
    ((emphasis === 'tickets' && guildStatus.guildRoles?.hasFreeTickets) ||
      (emphasis === 'early-access' && guildStatus.guildRoles?.hasEarlyAccess))
  )
}

function getDestinationUrl(
  pageData: GuildMarketingOfferPageData,
  emphasis: GuildSignupEmphasis,
  query: ParsedUrlQuery,
) {
  let destination = pageData.guildMemberHref ?? `/guild#${emphasis}`
  if (query['tv-post-auth'] === 'True') {
    const hasQuery = destination.includes('?')
    destination = `${destination}${hasQuery ? '&' : '?'}tv-post-auth=True`
  }
  return destination
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, preview, query, userId, params, req, res, region }) => {
    const slug = getItemOrFirstEntry(params?.slug)
    const isSupportLink = query.special === 'last-chance'

    if (!slug) {
      return { redirect: { destination: paths.guild.join, permanent: false } }
    }

    const [pageData, guildStatus] = await Promise.all([
      getMarketingOfferPageBySlug({ slug, opts: { locale, preview } }),
      getGuildStatus(federationClient),
    ])

    if (!pageData) {
      return {
        redirect: {
          destination: paths.guild.join,
          permanent: false,
        },
      }
    }

    if (pageData.redirectTo) {
      return {
        redirect: {
          destination: pageData.redirectTo,
          permanent: false,
        },
      }
    }

    const emphasis = pageData.emphasis || 'early-access'
    if (shouldRedirectToDashboard(guildStatus, emphasis)) {
      return {
        redirect: {
          destination: getDestinationUrl(pageData, emphasis, query),
          permanent: false,
        },
      }
    }

    const layout = pageData.layout
    const guildExplainerLayout = {
      ...pageData.explainerLayout,
      title: 'Continue this incredible journey with us.',
    }

    const sale = await getOffer(federationClient, pageData?.sale)

    const experimentKeys: ExperimentParamsArray = [
      { experimentKey: 'thousands-joined-follow-up', defaultValue: 'control' },
    ]

    if (slug === 'drybar') {
      experimentKeys.push({ experimentKey: 'free-rail-on-drybar-marketing-offer', defaultValue: false })
    }

    const opts = { locale, preview }
    const contentfulClient = getContentfulClient({ preview, locale })
    const [promotion, guildPricingTicketsOption, experiments, translations] = await Promise.all([
      getGuildPromotion({ opts: { locale, preview }, isSupportLink, federationClient }),
      getGuildPricingTicketsOption({ contentfulClient, opts }),
      runExperimentsOnServer(req, res, experimentKeys),
      loadTranslations(locale, ['common', 'join-the-guild', 'guild', 'guild-benefits', 'app-promo', 'watch']),
    ])

    let page = null
    let pageDataContext = null

    if (experiments['free-rail-on-drybar-marketing-offer']) {
      page = await getPageByPath('/m/offer/drybar', { locale, preview })
      pageDataContext = await fetchPageDataContext(page, preview, locale, federationClient)
    }

    return {
      props: {
        emphasis,
        guildExplainerLayout,
        guildMemberCta: pageData.guildMemberCta,
        guildMemberHref: pageData.guildMemberHref,
        layout,
        locale,
        projectSlugs: pageData.projectSlugs,
        promotion,
        sale: omitUndefineds(sale),
        slug,
        hasUser: Boolean(userId),
        experiments,
        isDarkMode: layout?.__typename !== 'GuildSignupSimpleLayout',
        region,
        page,
        pageDataContext,
        guildPricingTicketsOption,
        ...translations,
      },
    }
  },
)

GuildMarketingOfferPage.getLayout = (page, pageProps) => {
  return <DefaultLayout isDarkMode={Boolean(pageProps?.isDarkMode)}>{page}</DefaultLayout>
}

export default GuildMarketingOfferPage

async function getOffer(federationClient: ApolloClient, sale: ValidSale) {
  return await transformContentfulSaleToValidatedSale(sale, federationClient)
}
